/**
 * Time Store
 * Manages time tracking state
 */

import { create } from 'zustand';
import {
  startTimer,
  stopTimer,
  getRunningTimer,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  subscribeToTimeEntries,
} from '../services/timeEntryService';

export const useTimeStore = create((set, get) => ({
  timeEntries: [],
  runningTimer: null,
  loading: false,
  error: null,

  // Subscribe to time entries
  subscribeToEntries: (userId, isAdmin = false) => {
    return subscribeToTimeEntries(userId, (entries) => {
      set({ timeEntries: entries });

      // Check for running timer
      const running = entries.find(e => e.isRunning);
      if (running) {
        set({ runningTimer: running });
      } else if (get().runningTimer) {
        set({ runningTimer: null });
      }
    }, isAdmin);
  },

  // Start a new timer
  startTimer: async (userId, clientId, description) => {
    try {
      set({ loading: true, error: null });
      const result = await startTimer(userId, clientId, description);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Stop the running timer
  stopTimer: async (entryId) => {
    try {
      set({ loading: true, error: null });
      const result = await stopTimer(entryId);
      set({ runningTimer: null, loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Create manual time entry
  createEntry: async (userId, data) => {
    try {
      set({ loading: true, error: null });
      const result = await createTimeEntry(userId, data);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Update time entry
  updateEntry: async (entryId, data) => {
    try {
      set({ loading: true, error: null });
      const result = await updateTimeEntry(entryId, data);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Delete time entry
  deleteEntry: async (entryId) => {
    try {
      set({ loading: true, error: null });
      const result = await deleteTimeEntry(entryId);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
}));
